/**
 * Hit-testing utilities for canvas interactions.
 * All functions are pure — they take states/transitions as parameters.
 */

/**
 * Find a state at the given world coordinates. Returns the state or null.
 */
export function getStateAtPosition(states, x, y) {
  for (let i = states.length - 1; i >= 0; i--) {
    const state = states[i];
    const dx = x - state.x;
    const dy = y - state.y;
    if (dx * dx + dy * dy <= state.radius * state.radius) {
      return state;
    }
  }
  return null;
}

/**
 * Calculate transition geometry for hit testing and curve dragging.
 */
export function getTransitionGeometry(states, transitions, transition) {
  const fromState = states.find(s => s.id === transition.from_state);
  const toState = states.find(s => s.id === transition.to_state);

  if (!fromState || !toState) return null;

  if (fromState.id === toState.id) {
    // Self-loop geometry (rotatable ellipse)
    const loopWidth = 20;
    const loopHeight = 30;
    const loopAngle = transition.loopAngle !== undefined ? transition.loopAngle : -Math.PI / 2;
    const d = fromState.radius + loopHeight - 5;
    const cx = fromState.x + Math.cos(loopAngle) * d;
    const cy = fromState.y + Math.sin(loopAngle) * d;
    const rotation = loopAngle + Math.PI / 2;
    // Handle at far end of ellipse (local t = -PI/2)
    const handleX = cx + loopHeight * Math.sin(rotation);
    const handleY = cy - loopHeight * Math.cos(rotation);
    return {
      type: 'self-loop',
      centerX: cx,
      centerY: cy,
      loopWidth,
      loopHeight,
      rotation,
      loopAngle,
      handleX,
      handleY,
      state: fromState,
    };
  } else {
    // Regular transition geometry (Evan Wallace style)
    const dx = toState.x - fromState.x;
    const dy = toState.y - fromState.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Perpendicular direction
    const perpX = -dy / dist;
    const perpY = dx / dist;

    // Midpoint and control point
    const midX = (fromState.x + toState.x) / 2;
    const midY = (fromState.y + toState.y) / 2;
    let curve = transition.curve || 0;

    // Apply automatic bidirectional offset
    if (curve === 0) {
      const hasReverse = transitions.some(t =>
        t.from_state === transition.to_state &&
        t.to_state === transition.from_state
      );
      if (hasReverse) {
        curve = 1.2;
      }
    }

    const curveOffset = curve * dist * 0.5;
    const ctrlX = midX + perpX * curveOffset;
    const ctrlY = midY + perpY * curveOffset;

    // Start point: where line from ctrl to from.center intersects circle
    const fromToCtrlDx = ctrlX - fromState.x;
    const fromToCtrlDy = ctrlY - fromState.y;
    const fromToCtrlDist = Math.sqrt(fromToCtrlDx * fromToCtrlDx + fromToCtrlDy * fromToCtrlDy);
    const startX = fromState.x + (fromToCtrlDx / fromToCtrlDist) * fromState.radius;
    const startY = fromState.y + (fromToCtrlDy / fromToCtrlDist) * fromState.radius;

    // End point: where line from ctrl to to.center intersects circle
    const ctrlToToDx = toState.x - ctrlX;
    const ctrlToToDy = toState.y - ctrlY;
    const ctrlToToDist = Math.sqrt(ctrlToToDx * ctrlToToDx + ctrlToToDy * ctrlToToDy);
    const endX = toState.x - (ctrlToToDx / ctrlToToDist) * toState.radius;
    const endY = toState.y - (ctrlToToDy / ctrlToToDist) * toState.radius;

    // Compute label position (matches drawingHelpers rendering)
    const arcMidX = (startX + 2 * ctrlX + endX) / 4;
    const arcMidY = (startY + 2 * ctrlY + endY) / 4;
    const labelGap = 10;
    const sign = curveOffset >= 0 ? 1 : -1;
    const nx = perpX * sign;
    const ny = perpY * sign;
    // Approximate label half-size for offset (matches drawing logic)
    const halfProj = Math.abs(nx) * 30 + Math.abs(ny) * 10;
    const labelX = arcMidX + nx * (labelGap + halfProj);
    const labelY = arcMidY + ny * (labelGap + halfProj);

    return {
      type: 'curve',
      startX, startY,
      endX, endY,
      ctrlX, ctrlY,
      midX, midY,
      perpX, perpY,
      labelX, labelY,
      dist,
      from: fromState,
      to: toState,
    };
  }
}

/**
 * Find a transition at the given world coordinates.
 * Checks both the curve body and the label area.
 */
export function getTransitionAtPosition(states, transitions, x, y) {
  for (let i = transitions.length - 1; i >= 0; i--) {
    const transition = transitions[i];
    const geo = getTransitionGeometry(states, transitions, transition);
    if (!geo) continue;

    if (geo.type === 'self-loop') {
      // Hit test rotated ellipse: un-rotate the point, then check
      const dx = x - geo.centerX;
      const dy = y - geo.centerY;
      const cosR = Math.cos(-geo.rotation);
      const sinR = Math.sin(-geo.rotation);
      const localX = dx * cosR - dy * sinR;
      const localY = dx * sinR + dy * cosR;
      const normX = localX / geo.loopWidth;
      const normY = localY / geo.loopHeight;
      const ellipseDist = Math.sqrt(normX * normX + normY * normY);

      if (ellipseDist > 0.6 && ellipseDist < 1.4) {
        return transition;
      }
      // Also check near the label area (at far end of ellipse)
      const labelX = geo.handleX;
      const labelY = geo.handleY;
      if (Math.abs(x - labelX) < 40 && Math.abs(y - labelY) < 25) {
        return transition;
      }
    } else {
      // Check if point is near the bezier curve (sample points along curve)
      for (let t = 0; t <= 1; t += 0.02) {
        const px = (1 - t) * (1 - t) * geo.startX + 2 * (1 - t) * t * geo.ctrlX + t * t * geo.endX;
        const py = (1 - t) * (1 - t) * geo.startY + 2 * (1 - t) * t * geo.ctrlY + t * t * geo.endY;
        const dist = Math.sqrt((x - px) * (x - px) + (y - py) * (y - py));
        if (dist < 20) {
          return transition;
        }
      }
      // Also check near the label area
      if (Math.abs(x - geo.labelX) < 40 && Math.abs(y - geo.labelY) < 20) {
        return transition;
      }
    }
  }
  return null;
}

/**
 * Find a transition whose LABEL is at the given world coordinates.
 * Returns the transition or null. Used to distinguish label clicks from arc clicks.
 */
export function getTransitionLabelAtPosition(states, transitions, x, y) {
  for (let i = transitions.length - 1; i >= 0; i--) {
    const transition = transitions[i];
    const geo = getTransitionGeometry(states, transitions, transition);
    if (!geo) continue;

    if (geo.type === 'self-loop') {
      const labelX = geo.handleX;
      const labelY = geo.handleY;
      if (Math.abs(x - labelX) < 40 && Math.abs(y - labelY) < 25) {
        return transition;
      }
    } else {
      if (Math.abs(x - geo.labelX) < 40 && Math.abs(y - geo.labelY) < 20) {
        return transition;
      }
    }
  }
  return null;
}


/**
 * Find a curve handle at the given world coordinates (for drag-to-reshape).
 */
export function getCurveHandleAtPosition(states, transitions, x, y) {
  for (const transition of transitions) {
    const geo = getTransitionGeometry(states, transitions, transition);
    if (!geo) continue;

    if (geo.type === 'self-loop') {
      const dist = Math.sqrt((x - geo.handleX) * (x - geo.handleX) + (y - geo.handleY) * (y - geo.handleY));
      if (dist < 12) {
        return transition;
      }
    } else {
      const dist = Math.sqrt((x - geo.ctrlX) * (x - geo.ctrlX) + (y - geo.ctrlY) * (y - geo.ctrlY));
      if (dist < 12) {
        return transition;
      }
    }
  }
  return null;
}
